import { once } from 'lodash';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext
} from 'react';

import { EntitySchema } from '@sierralabs/entity-core';
import { FormElementMeta, FormSectionMeta } from '@sierralabs/forms-core';
import {
  StateEventConfig,
  StateMachine,
  StateNode,
  StateNodeConfig
} from '@sierralabs/state-machine';

import { FetchError } from '../api/api';
import { ActionPlugin } from './plugins/ActionPlugin';
import { FormElementPlugin } from './plugins/FormElementPlugin';
import { TransitionConditionPlugin } from './plugins/TransitionConditionPlugin';
import { User } from '../models/User';

export interface DynamicFormState<FormValuesType, FormElementConfigType> {
  selectedStateNode?: StateNodeConfig<
    FormSectionMeta<FormValuesType, FormElementConfigType>,
    any
  >;
  selectedFormElement?: FormElementMeta<FormValuesType, FormElementConfigType>;
  lastFormElementType: string;
  isSubmitting: boolean;
}

export type ChangedFormValue<FormValuesType> = {
  [key: string]: FormValuesType[keyof FormValuesType];
};

export type SubscribeTransitionFunc<FormValuesType, FormElementConfigType> = (
  eventType: 'transition' | 'final',
  callback: (
    context: DynamicFormContextState<FormValuesType, FormElementConfigType>,
    event: StateEventConfig
  ) => Promise<void>
) => void;
export type SubscribeTransitionEmitFunc = (
  type: 'transition' | 'final',
  event: StateEventConfig
) => Promise<void>;

// export type SubscribeFormEvent = 'transition';
// export type SubscribeFormCallback<event extends SubscribeFormEvent, FormValuesType, FormElementConfigType> = (
//   context: DynamicFormContextState<FormValuesType, FormElementConfigType>,
//   value: event === 'tranisition' ? string : unknown
// ) => void;

export type DynamicFormContextState<
  FormValuesType,
  FormElementConfigType,
  DataSourceContext = any
> = {
  user?: User;
  entitySchema?: EntitySchema;
  formState: DynamicFormState<FormValuesType, FormElementConfigType>;
  formSections: StateNodeConfig<
    FormSectionMeta<FormValuesType, FormElementConfigType>,
    any
  >[];
  loadingElementQueue: FormElementMeta<any, any>[];
  setLoadingElementQueue: React.Dispatch<
    React.SetStateAction<FormElementMeta<any, any>[]>
  >;
  /**
   * Accessible from the form context (ex. used in the AssignFormAction plugin)
   */
  studyParticipantId?: number;
  isSubmitting?: boolean;
  error?: Error | FetchError;
  formValues: Partial<FormValuesType>;
  previousFormValues: Partial<FormValuesType>;
  changedFormValue?: ChangedFormValue<FormValuesType>;
  stateMachine?: StateMachine;
  transitioningStateEvent?: StateEventConfig | null;
  setTransitioningStateEvent: Dispatch<
    SetStateAction<StateEventConfig | undefined | null>
  >;
  setFormState: Dispatch<
    SetStateAction<DynamicFormState<FormValuesType, FormElementConfigType>>
  >;
  setFormValues: Dispatch<SetStateAction<Partial<FormValuesType>>>;
  setFormValue: (
    key: keyof FormValuesType,
    value: FormValuesType[keyof FormValuesType],
    element?: FormElementMeta<FormValuesType, FormElementConfigType>
  ) => void;
  clearFormValues: (keys?: (keyof FormValuesType)[]) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
  setError: (error: Error | FetchError) => void;
  elementPlugins: FormElementPlugin<any, any>[];
  transitionConditionPlugins: TransitionConditionPlugin<any>[];
  actionPlugins: ActionPlugin<any>[];
  subscribe: SubscribeTransitionFunc<FormValuesType, FormElementConfigType>;
  unsubscribe: SubscribeTransitionFunc<FormValuesType, FormElementConfigType>;
  emitEvent: SubscribeTransitionEmitFunc;
  transition: (event: StateEventConfig) => void;
  isScrollEnabled: boolean;
  setIsScrollEnabled: (isScrollEnabled: boolean) => void;
  activeStateNodes: StateNode<FormSectionMeta<FormValuesType, any>, any>[];
  setActiveStateNodes: React.Dispatch<
    React.SetStateAction<StateNode<FormSectionMeta<FormValuesType, any>, any>[]>
  >;
  /**
   * Allow form elements to disable form event transitions (ex. when uploading a file)
   */
  disableEventTransition: boolean;
  setDisableEventTransition: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Pass through any DataSourceContext metadata (ex. used in AssignFormAction plugin)
   */
  dataSourceContext?: DataSourceContext;
};

export const createDynamicFormContext = once(<
  FormValuesType,
  FormElementConfigType
>() =>
  createContext<DynamicFormContextState<FormValuesType, FormElementConfigType>>(
    null!
  )
);

export const useDynamicFormContext = <
  FormValuesType,
  FormElementConfigType
>() => {
  const context = useContext(
    createDynamicFormContext<FormValuesType, FormElementConfigType>()
  );
  if (!context) {
    throw new Error(
      'useDynamicFormContext must be used within a DynamicFormProvider'
    );
  }
  return context;
};
